/**
 * speciesVariants — resolve which Pokédex variants are supported for a single
 * national-dex species, for the per-Pokémon detail page (/pokemon/:dexNumber).
 *
 * The detail page renders one tile per SUPPORTED variant. "Supported" means:
 *   - Full-National-Dex variants (Default / Shiny / Lucky / Perfect / XXL /
 *     XXS) — every released species can in principle be obtained in these, so
 *     they are supported for any id in 1..NATIONAL_DEX_TOTAL.
 *   - Curated-roster variants (Mega / G-Max / Shadow / Purified) — only the
 *     species that actually have that form in Pokémon GO, gated by the curated
 *     roster in pogoAvailability (validateDexMember / curatedRosterFor).
 *   - Manual-only custom dexes (Shiny Perfect / Shiny Lucky) — these are
 *     user-tracked combinations of the full-dex variants, so they apply to any
 *     id, exactly like the full-dex variants.
 *
 * Ordering follows ALL_DEX_VARIANTS_ORDERED (the canonical carousel order +
 * manual-only extras) so the tile grid matches the dex pill order the user is
 * used to. Visibility (Settings hide/show) is intentionally NOT applied here:
 * the detail page shows every variant a species supports, independent of which
 * pills the user has chosen to surface on the main dex.
 */

import {
  ALL_VARIANTS,
  FORM_DEX_VARIANTS,
  MANUAL_ONLY_VARIANTS,
  type PokedexVariant,
} from "./pogoPokedexVariantClassifier";
import { hasCuratedRoster, validateDexMember } from "./pogoAvailability";
import { NATIONAL_DEX_TOTAL } from "./constants";

/**
 * Canonical order for the detail-page variant grid: the scannable variants in
 * carousel order followed by the manual-only custom dexes. Mirrors
 * dexVisibilityStore.ALL_DEX_VARIANTS_ORDERED but is declared locally so this
 * module carries no dependency on the visibility store.
 */
export const DETAIL_VARIANT_ORDER: readonly PokedexVariant[] = [
  ...ALL_VARIANTS,
  // The form dexes are excluded: their cells are FORMS, so one detail tile
  // cannot stand for a species' four costumes, and the species page already
  // lists forms and costumes in their own sections below the tile grid.
  ...MANUAL_ONLY_VARIANTS.filter((v) => !FORM_DEX_VARIANTS.includes(v)),
];

/** True when `dexNumber` is a valid national-dex id (1..NATIONAL_DEX_TOTAL). */
export function isValidDexNumber(dexNumber: number): boolean {
  return (
    Number.isInteger(dexNumber) &&
    dexNumber >= 1 &&
    dexNumber <= NATIONAL_DEX_TOTAL
  );
}

/**
 * Whether a single variant is supported for a species. Curated-roster variants
 * defer to validateDexMember; every other variant is supported for any valid id.
 */
export function variantSupportsSpecies(
  variant: PokedexVariant,
  dexNumber: number,
): boolean {
  if (!isValidDexNumber(dexNumber)) return false;
  if (hasCuratedRoster(variant)) {
    return validateDexMember(variant, dexNumber).valid;
  }
  return true;
}

/**
 * Ordered list of every variant supported for the given species, for the
 * detail-page tile grid. Returns an empty array for an invalid dex number.
 */
export function supportedVariantsForSpecies(
  dexNumber: number,
): PokedexVariant[] {
  if (!isValidDexNumber(dexNumber)) return [];
  return DETAIL_VARIANT_ORDER.filter((v) => variantSupportsSpecies(v, dexNumber));
}

/**
 * Clamp helper for national-dex prev/next navigation. Returns the adjacent id
 * in `dir` (+1 next, -1 prev), clamped at 1 and NATIONAL_DEX_TOTAL (no wrap),
 * matching the Dex carousel's clamp-no-wrap convention. Returns the input id
 * unchanged when it is already at the clamped edge or is itself invalid.
 */
export function adjacentDexNumber(dexNumber: number, dir: 1 | -1): number {
  if (!isValidDexNumber(dexNumber)) return dexNumber;
  const next = dexNumber + dir;
  if (next < 1 || next > NATIONAL_DEX_TOTAL) return dexNumber;
  return next;
}
